import api from "@/utils/axios";
import identityApi from "@utils/axiosIdentity";
import router from "@/router";
import { useToast } from "vue-toastification";
import { i18n } from "@/libs/i18n";

const toast = useToast();

const state = {
  users: [],
  userPermissions: [],
  pagination: {
    totalObjects: 0,
    perPage: 5,
    currentPage: 1,
  },
  userSearchString: null,
  user: {},
  accountType: "",
  passwordPolicies: "",
  isLoading: false,
};

const getters = {
  users(state) {
    return state.users;
  },
  userPermissions(state) {
    return state.userPermissions;
  },
  user(state) {
    return state.user;
  },
  totalUsers(state) {
    return state.pagination.total;
  },
};

const mutations = {
  ADD_USER_FORM_TO_TABLE(state) {
    if (!state.users[0].toBeCreated) {
      state.users.unshift({
        name: "",
        email: "",
        username: "",
        active_status: true,
        loggin_attempts: 0,
        toBeCreated: true,
      });
    }
  },
  CANCEL_ADD_USER_FORM_TO_TABLE(state) {
    if (state.users[0].toBeCreated) {
      state.users.shift();
    }
  },
  EDIT_NEW_USER(state, { field, value }) {
    if (state.users[0].toBeCreated) {
      state.users[0][field] = value;
    }
  },
  SET_USERS(state, payload) {
    state.users = payload;
  },
  SET_USER_PERMISSIONS(state, payload) {
    state.userPermissions = payload;
  },
  SET_USER(state, payload) {
    state.user = payload;
  },
  SET_USER_FIELD(state, { field, value }) {
    state.user[field] = value;
  },
  SET_PAGINATION(state, { field, value }) {
    state.pagination[field] = value;
  },
  SET_USER_SEARCH_STRING(state, value) {
    state.userSearchString = value;
  },
  TOGGLE_USER_STATUS(state, { user, status }) {
    user.active = status;
  },
  SET_ACCOUNT_TYPE(state, type) {
    state.accountType = type;
  },
  SET_PASSWORD_POLICIES(state, policies) {
    state.passwordPolicies = policies;
  },
  CANCEL_USER_GENERAL_INFO(state) {
    router.back();
  },
  SET_LOADING(state, value) {
    state.isLoading = value;
  },
};

const actions = {
  getUsers(context, { filterByName = null, orderBy = null }) {
    context.commit("SET_LOADING", true);
    api
      .get("/users", {
        params: {
          PerPage: context.state.pagination.perPage,
          CurrentPage: context.state.pagination.currentPage,
          FilterByName: filterByName,
          OrderBy: orderBy,
        },
      })
      .then((response) => {
        context.commit("SET_USERS", response.data.results);
        context.commit("SET_PAGINATION", {
          field: "totalObjects",
          value: response.data.totalObjects,
        });
        context.commit("SET_PAGINATION", {
          field: "currentPage",
          value: response.data.currentPage,
        });
        context.commit("SET_LOADING", false);
      });
  },

  getUser(context, { id }) {
    return new Promise((resolve, reject) => {
      api
        .get(`/users/${id}`)
        .then((response) => {
          context.commit("SET_USER", response.data);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  createUser(context, payload) {
    return new Promise((resolve, reject) => {
      api
        .post("/users", payload)
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

updateUser(context, payload) {
  return new Promise((resolve, reject) => {
    // Validar payload requerido
    if (!payload || !payload.id) {
      reject(new Error("ID de usuario es requerido"));
      return;
    }

    // Preparar payload
    const updatePayload = {
      name: payload.name || null,
      email: payload.email || null,
      userName: payload.userName || null,
      paternalSurName: payload.paternalSurName || null,
      maternalSurName: payload.maternalSurName || null,
      culture: payload.culture || "es-MX",
      timeZone: payload.timeZone || "America/Mexico_City"
    };

    // Validar campos mínimos requeridos
    if (!updatePayload.name || !updatePayload.email || !updatePayload.userName) {
      reject(new Error("Nombre, email y nombre de usuario son campos requeridos"));
      return;
    }

    api
      .post(`/users/update_user_settings/${payload.id}`, updatePayload)
      .then((response) => {
        // Actualizar el usuario en el store localmente
        if (response.data) {
          context.commit("SET_USER", response.data);
        }
        resolve(response);
      })
      .catch((error) => {
        // Mejor manejo de errores
        const errorMessage = error.response?.data?.detail ||
                           error.response?.data?.message ||
                           error.message ||
                           "Error desconocido al actualizar usuario";

        console.error(`Error actualizando usuario ${payload.id}:`, {
          error: errorMessage,
          payload: updatePayload,
          status: error.response?.status
        });

        reject(new Error(errorMessage));
      });
  });
},

  getUserAccountType(context, payload) {
    return new Promise((resolve, reject) => {
      api
        .get("/users/get_account_type")
        .then((response) => {
          context.commit("SET_ACCOUNT_TYPE", response.data);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getUserAdminPriviligies(context, { userId, applicationId }) {
    return new Promise((resolve, reject) => {
      api
        .get(`/users/${userId}/get_admin_privileges`, {
          params: {
            ApplicationId: applicationId,
          },
        })
        .then((response) => {
          context.commit("SET_USER_FIELD", {
            field: "isAdmin",
            value: response.data,
          });
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  toggleUserAdminPriviligies(context, { userId, applicationId }) {
    return new Promise((resolve, reject) => {
      // Si applicationId es undefined, usar 'Framework' como valor por defecto
      const appId = applicationId || 'Framework'

      api
        .post(
          `/users/${userId}/toggle_admin_privileges`,
          {},
          {
            params: {
              ApplicationId: appId
            }
          }
        )
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },

  getPasswordPolicies(context, payload) {
    return new Promise((resolve, reject) => {
      identityApi
      .get("/get_policies")
        .then((response) => {
          context.commit("SET_PASSWORD_POLICIES", response.data);
          this.passwordPolicies = response.data;
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  toggleUserStatus(context, { id, status }) {
    return new Promise((resolve, reject) => {
      api
        .post(
          `/v1/users/${id}/toggle_active_status`,
          {},
          {
            params: {
              activeStatus: status,
            },
          }
        )
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  changeUserPassword(context, payload) {
    return new Promise((resolve, reject) => {
      identityApi
        .put(
          "/v1/users/change_password",
          {
            id: payload.id,
            password: payload.password,
            confirmPassword: payload.confirmPassword,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        )
        .then((response) => {
          toast.success(i18n.global.t("successMessages.passwordUpdated"));
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  resetLogginAttempts(context, { userId }) {
    return new Promise((resolve, reject) => {
      api
        .post(`/v1/users/${userId}/reset_login_attempts`)
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getUserPermissions(context, { applicationId, userId }) {
    context.commit("SET_LOADING", true);
    return new Promise((resolve, reject) => {
      api
        .get("/users/get_permission_detail", {
          params: {
            ApplicationId: applicationId,
            UserId: userId,
          },
        })
        .then((response) => {
          context.commit("SET_USER_PERMISSIONS", response.data);
          resolve(response);
        })
        .catch((error) => {
          context.commit("SET_USER_PERMISSIONS", []);
          reject(error);
        })
        .finally(() => {
          context.commit("SET_LOADING", false);
        });
    });
  },

  saveUserPermissions(context, { userPermissions }) {
    return new Promise((resolve, reject) => {
      api
        .post("/v1/users/save_user_permission", userPermissions)
        .then((response) => {
          context.commit("SET_USER_PERMISSIONS", response.data);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getAccessUser(_, { FilterName }) {
    return new Promise((resolve, reject) => {
      api
        .get(`/users`, {
          params: {
            FilterByName: FilterName,
          },
        })
        .then((response) => {
          resolve(response.data.results);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  unassignUserAllApplications(_, { id }) {
    return new Promise((resolve, reject) => {
      identityApi
        .delete(`/v1/users/${id}/unassign_user_from_all_applications`)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  createNewUser(_, { payload }) {
    return new Promise((resolve, reject) => {
      api
        .post(`/v1/users`, payload)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
